import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { services } from '../data/services';

/* ── Stat card ───────────────────────────────────────────────── */
const StatItem = ({ icon, value, suffix, label }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '1.5rem 1rem' }}
    >
      {icon && (
        <div style={{ color: '#F5C300', marginBottom: '0.6rem' }}>{icon}</div>
      )}
      <span style={{ fontFamily: '"Barlow Condensed", sans-serif', color: '#F5C300', fontWeight: 800, fontSize: 'clamp(2rem, 4vw, 2.8rem)', lineHeight: 1 }}>
        {value}{suffix}
      </span>
      <span style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem', marginTop: '0.4rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}
      </span>
    </motion.div>
  );
};

/* ── Service card (grid) ────────────────────────────────────── */
const ServiceCard = ({ icon, title, description, delay }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{
        background: '#F9F9F9',
        padding: '1.75rem',
        borderRadius: '12px',
        borderTop: `4px solid ${hovered ? '#F5C300' : 'transparent'}`,
        transition: 'border-color 0.3s',
        cursor: 'default',
      }}
    >
      <div style={{ color: '#F5C300', marginBottom: '1rem' }}>{icon}</div>
      <h3 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700, fontSize: '1.3rem', color: '#1A1A1A', marginBottom: '0.5rem', letterSpacing: '0.03em' }}>
        {title}
      </h3>
      <p style={{ fontFamily: 'Inter, sans-serif', color: '#555', fontSize: '0.875rem', lineHeight: 1.7, margin: 0 }}>
        {description}
      </p>
    </motion.div>
  );
};

/* ── Testimonials ────────────────────────────────────────────── */
const testimonials = [
  {
    name: 'Mike T',
    location: 'Porirua',
    text: 'North City have been looking after all our family vehicles for years. Always honest, always reliable, and the work is done right the first time. Wouldn\'t take my car anywhere else in Porirua.',
  },
  {
    name: 'Sarah B',
    location: 'Porirua',
    text: 'Came in for a WOF and they picked up a couple of things I didn\'t know about. They explained everything clearly and didn\'t try to upsell me on things I didn\'t need. Really trustworthy team.',
  },
  {
    name: 'James K',
    location: 'Porirua',
    text: 'Had the 3D wheel alignment done and the difference was noticeable straight away. Friendly staff, great service and fair pricing. Highly recommend to anyone in the Porirua area.',
  },
];

/* ── HOME PAGE ───────────────────────────────────────────────── */
const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const heroWords = ['Your', 'Local', 'Workshop', 'in', 'Porirua.'];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const gridServices = services.slice(0, 3);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      style={{ paddingTop: '80px' }}
    >
      {/* ── HERO ──────────────────────────────────────────────── */}
      <section style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'flex-end', paddingBottom: '5rem' }}>
        {/* Hero background image */}
        <img
          src="/images/workshop-exterior.webp"
          alt="North City Service and Tyre Centre exterior in Porirua"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
        />

        {/* Dark gradient overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, #1A1A1A 30%, rgba(26,26,26,0.55) 70%, rgba(26,26,26,0.2) 100%)' }} />

        {/* Hero content */}
        <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{
            maxWidth: '620px',
            background: 'rgba(26,26,26,0.82)',
            backdropFilter: 'blur(12px)',
            padding: 'clamp(1.5rem, 4vw, 2.5rem)',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            {/* Animated headline */}
            <h1 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, lineHeight: 1.05, marginBottom: '1.25rem', marginTop: 0, fontSize: 'clamp(2.8rem, 6vw, 5rem)' }}>
              {heroWords.map((word, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: i * 0.1, ease: 'easeOut' }}
                  style={{
                    display: 'inline-block',
                    marginRight: '0.25em',
                    color: word === 'Porirua.' ? '#F5C300' : '#ffffff',
                  }}
                >
                  {word}
                </motion.span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.78)', fontSize: 'clamp(0.95rem, 2vw, 1.1rem)', lineHeight: 1.7, marginBottom: '2rem', marginTop: 0 }}
            >
              Family owned, MTA assured, and built on trust. From WOFs to full engine servicing, we keep Porirua moving.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}
            >
              <Link
                to="/contact"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  background: '#F5C300', color: '#1A1A1A',
                  fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700,
                  fontSize: '1rem', letterSpacing: '0.08em',
                  padding: '0.75rem 2rem', borderRadius: '9999px', textDecoration: 'none',
                  transition: 'background 0.25s, transform 0.25s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#e6b800'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#F5C300'; e.currentTarget.style.transform = 'scale(1)'; }}
              >
                BOOK NOW
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                to="/services"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  background: 'transparent', color: '#ffffff',
                  fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700,
                  fontSize: '1rem', letterSpacing: '0.08em',
                  padding: '0.75rem 2rem', borderRadius: '9999px', textDecoration: 'none',
                  border: '2px solid rgba(255,255,255,0.7)',
                  transition: 'background 0.25s, border-color 0.25s, transform 0.25s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.7)'; e.currentTarget.style.transform = 'scale(1)'; }}
              >
                OUR SERVICES
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── STATS STRIP ─────────────────────────────────────── */}
      <section style={{ background: '#1A1A1A', padding: '3rem 1.5rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }} className="md-grid-4">
          <style>{`@media(min-width:768px){.md-grid-4{grid-template-columns:repeat(4,1fr)!important;border-top:1px solid rgba(255,255,255,0.08);}}`}</style>

          <StatItem
            icon={
              <svg width="36" height="36" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7L12 2zm-2 13l-4-4 1.41-1.41L10 12.17l6.59-6.59L18 7l-8 8z" />
              </svg>
            }
            value="MTA"
            suffix=""
            label="Assured Workshop"
          />
          <StatItem
            icon={
              <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
                <circle cx="12" cy="12" r="3" />
                <circle cx="12" cy="12" r="8" />
                <path strokeLinecap="round" d="M12 2v2M12 20v2M2 12h2M20 12h2" />
              </svg>
            }
            value="3D"
            suffix=""
            label="Laser Wheel Alignment"
          />
          <StatItem
            icon={
              <svg width="36" height="36" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
              </svg>
            }
            value="Family"
            suffix=""
            label="Owned and Operated"
          />
          <StatItem
            icon={
              <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
                <rect x="2" y="7" width="18" height="11" rx="2" />
                <path strokeLinecap="round" d="M20 10h2v5h-2M7 11v3M10 11v3M13 11v3" />
              </svg>
            }
            value="FREE"
            suffix=""
            label="Battery Check Included"
          />
        </div>
      </section>

      {/* ── SERVICES GRID ───────────────────────────────────── */}
      <section style={{ padding: 'clamp(3rem, 6vw, 5rem) 1.5rem', background: '#ffffff' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '3rem' }}
          >
            <span style={{ fontFamily: 'Inter, sans-serif', color: '#F5C300', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>Our Expertise</span>
            <h2 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(2.5rem, 5vw, 4rem)', color: '#1A1A1A', margin: '0.3rem 0 0.75rem', letterSpacing: '0.02em' }}>
              What We Do
            </h2>
            <div style={{ width: '48px', height: '4px', background: '#F5C300', margin: '0 auto' }} />
          </motion.div>

          <style>{`.home-services-grid{grid-template-columns:1fr;}@media(min-width:768px){.home-services-grid{grid-template-columns:repeat(3,1fr);}}`}</style>
          <div className="home-services-grid" style={{ display: 'grid', gap: '1.25rem' }}>
            {gridServices.map((s, i) => (
              <ServiceCard
                key={s.id}
                icon={s.icon}
                title={s.title}
                description={s.shortDesc}
                delay={(i % 3) * 0.08}
              />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginTop: '2.5rem' }}
          >
            <Link
              to="/services"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: 'transparent', color: '#1A1A1A',
                fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700,
                fontSize: '1rem', letterSpacing: '0.08em',
                padding: '0.75rem 2rem', borderRadius: '9999px', textDecoration: 'none',
                border: '2px solid #1A1A1A',
                transition: 'background 0.25s, color 0.25s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1A1A1A'; e.currentTarget.style.color = '#ffffff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#1A1A1A'; }}
            >
              VIEW ALL SERVICES
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── WHY NORTH CITY ─────────────────────────────────── */}
      <section style={{ background: '#1A1A1A', padding: 'clamp(3rem, 6vw, 5rem) 1.5rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '3rem' }}
          >
            <span style={{ fontFamily: 'Inter, sans-serif', color: '#F5C300', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>The Difference</span>
            <h2 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(2.5rem, 5vw, 4rem)', color: '#ffffff', margin: '0.3rem 0 0.75rem' }}>
              Why North City?
            </h2>
            <div style={{ width: '48px', height: '4px', background: '#F5C300', margin: '0 auto' }} />
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '2rem' }}>
            {[
              {
                icon: (
                  <svg width="48" height="48" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7L12 2zm-2 13l-4-4 1.41-1.41L10 12.17l6.59-6.59L18 7l-8 8z" />
                  </svg>
                ),
                title: 'MTA Assured',
                desc: 'We are a proud MTA Assured workshop. This means our repairs meet the highest industry standards and you can book with complete confidence.',
              },
              {
                icon: (
                  <svg width="48" height="48" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                  </svg>
                ),
                title: 'Family Owned',
                desc: 'We are a local Porirua family business. You deal with real people who care about the quality of the work and your satisfaction on every visit.',
              },
              {
                icon: (
                  <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ),
                title: 'Latest Equipment',
                desc: 'From 3D laser wheel alignment to full diagnostic scanning, we invest in the latest tools to diagnose and fix your vehicle accurately and efficiently.',
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                viewport={{ once: true }}
                style={{ textAlign: 'center', padding: '2rem 1.5rem' }}
              >
                <div style={{ color: '#F5C300', display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>{item.icon}</div>
                <h3 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700, fontSize: '1.75rem', color: '#ffffff', margin: '0 0 0.75rem', letterSpacing: '0.03em' }}>{item.title}</h3>
                <p style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, margin: 0, fontSize: '0.9rem' }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ────────────────────────────────────── */}
      <section style={{ background: '#F9F9F9', padding: 'clamp(3rem, 6vw, 5rem) 1.5rem' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '2.5rem' }}
          >
            <span style={{ fontFamily: 'Inter, sans-serif', color: '#F5C300', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>Reviews</span>
            <h2 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', color: '#1A1A1A', margin: '0.3rem 0 0.75rem' }}>
              What Customers Say
            </h2>
            <div style={{ width: '48px', height: '4px', background: '#F5C300', margin: '0 auto' }} />
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              style={{ background: '#ffffff', padding: 'clamp(1.75rem, 4vw, 3rem)', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', textAlign: 'center' }}
            >
              {/* Stars */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '1.5rem' }}>
                {[...Array(5)].map((_, i) => (
                  <svg key={i} width="20" height="20" fill="#F5C300" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <blockquote style={{ fontFamily: 'Inter, sans-serif', color: '#444', fontSize: 'clamp(1rem, 2.2vw, 1.2rem)', lineHeight: 1.8, fontStyle: 'italic', margin: '0 0 1.75rem' }}>
                "{testimonials[currentTestimonial].text}"
              </blockquote>
              <p style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700, fontSize: '1.2rem', color: '#1A1A1A', margin: '0 0 0.2rem', letterSpacing: '0.05em' }}>
                {testimonials[currentTestimonial].name}
              </p>
              <p style={{ fontFamily: 'Inter, sans-serif', color: '#888', fontSize: '0.85rem', margin: 0 }}>
                {testimonials[currentTestimonial].location}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '1.5rem' }}>
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentTestimonial(i)}
                aria-label={`Testimonial ${i + 1}`}
                style={{
                  width: i === currentTestimonial ? '28px' : '10px',
                  height: '10px',
                  borderRadius: '9999px',
                  background: i === currentTestimonial ? '#F5C300' : '#ccc',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  padding: 0,
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ──────────────────────────────────────── */}
      <section style={{ background: '#F5C300', padding: 'clamp(3rem, 6vw, 4.5rem) 1.5rem', textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(2.5rem, 5vw, 4rem)', color: '#1A1A1A', margin: '0 0 0.75rem' }}>
            Ready to Book?
          </h2>
          <p style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(26,26,26,0.75)', fontSize: '1.05rem', margin: '0 0 2rem' }}>
            Get in touch with the team today. We will get you sorted quickly.
          </p>
          <Link
            to="/contact"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: '#1A1A1A', color: '#ffffff',
              fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700,
              fontSize: '1.05rem', letterSpacing: '0.08em',
              padding: '0.85rem 2.5rem', borderRadius: '9999px', textDecoration: 'none',
              transition: 'background 0.25s, transform 0.25s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#333'; e.currentTarget.style.transform = 'scale(1.03)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#1A1A1A'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            BOOK NOW
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </motion.div>
      </section>
    </motion.div>
  );
};

export default Home;
